import { useTranslation } from 'react-i18next'
import { MapPin } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SupplierInstallationAddressFormColumn } from '@/features/suppliers/components/supplier-installation-address-form-column'
import type { SupplierInstallationFormState } from '@/features/suppliers/lib/supplier-installation-form-state'

type SupplierInstallationAddressSectionCardProps = {
  state: SupplierInstallationFormState
  countryName: string
  doesRequireLocalAddress: boolean
  latinErrorMessage: string
  variant?: 'card' | 'plain'
  onSingleCountryAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onCnEnglishAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onCnLocalAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onLocationFieldChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
}

export function SupplierInstallationAddressSectionCard({
  state,
  countryName,
  doesRequireLocalAddress,
  latinErrorMessage,
  variant = 'card',
  onSingleCountryAddressInput,
  onCnEnglishAddressInput,
  onCnLocalAddressInput,
  onLocationFieldChange,
}: SupplierInstallationAddressSectionCardProps) {
  const { t } = useTranslation(['settings', 'common', 'installations'])

  const fields = (
    <>
        {doesRequireLocalAddress ? (
          <>
            <Alert>
              <AlertDescription>
                {t('settings:organizationDetails.details.localAddressInfo')}
              </AlertDescription>
            </Alert>
            <div className="grid gap-4 lg:grid-cols-2">
              <SupplierInstallationAddressFormColumn
                isLocal
                countryCode={state.country}
                street={state.localStreet}
                streetNumber={state.localStreetNumber}
                street2={state.localStreet2}
                postCode={state.localPostCode}
                poBox={state.localPoBox}
                city={state.localCity}
                latinErrorMessage={latinErrorMessage}
                onChange={onCnLocalAddressInput}
                inputIdPrefix="local-"
              />
              <SupplierInstallationAddressFormColumn
                isLocal={false}
                countryCode={state.country}
                street={state.street}
                streetNumber={state.streetNumber}
                street2={state.street2}
                postCode={state.postCode}
                poBox={state.poBox}
                city={state.city}
                latinErrorMessage={latinErrorMessage}
                onChange={onCnEnglishAddressInput}
              />
            </div>
          </>
        ) : (
          <SupplierInstallationAddressFormColumn
            isLocal={false}
            countryCode={state.country}
            street={state.street}
            streetNumber={state.streetNumber}
            street2={state.street2}
            postCode={state.postCode}
            poBox={state.poBox}
            city={state.city}
            latinErrorMessage={latinErrorMessage}
            onChange={onSingleCountryAddressInput}
          />
        )}
        <div className="max-w-md">
          <Label>{t('common:inputFields.country.label')}</Label>
          <Input value={countryName} disabled className="mt-2" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="grid gap-2">
            <Label htmlFor="unlocode">
              {t('installations:suppliers.edit.installations.edit.fields.unlocode')}
            </Label>
            <Input id="unlocode" value={state.unlocode} onChange={onLocationFieldChange} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="longitude">
              {t('installations:suppliers.edit.installations.edit.fields.longitude')}
            </Label>
            <Input
              id="longitude"
              type="number"
              step="any"
              value={state.longitude}
              onChange={onLocationFieldChange}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="latitude">
              {t('installations:suppliers.edit.installations.edit.fields.latitude')}
            </Label>
            <Input
              id="latitude"
              type="number"
              step="any"
              value={state.latitude}
              onChange={onLocationFieldChange}
            />
          </div>
        </div>
    </>
  )

  if (variant === 'plain') {
    return <div className="space-y-4">{fields}</div>
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MapPin className="size-5 shrink-0" />
          <CardTitle>{t('settings:organizationDetails.details.address')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{fields}</CardContent>
    </Card>
  )
}
